import { Link } from 'react-router-dom';
import { ArrowRight, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useNewsPosts } from '@/hooks/useNewsPosts';
import { motion } from 'framer-motion';
import { format, parseISO } from 'date-fns';

// Clean title by removing site suffix (fallback for any missed in DB)
const cleanTitle = (title: string) => {
  return title.replace(/\s*[–-]\s*n4researchgroup.*$/i, '');
};

// Strip markdown formatting from text
const stripMarkdown = (text: string): string => {
  return text
    .replace(/\*\*(.*?)\*\*/g, '$1')   // **bold** → bold
    .replace(/\*(.*?)\*/g, '$1')        // *italic* → italic
    .replace(/\[(.*?)\]\(.*?\)/g, '$1') // [link](url) → link
    .replace(/`(.*?)`/g, '$1')          // `code` → code
    .replace(/#{1,6}\s/g, '');          // Remove heading markers
};

// Clean excerpt - if it's just "!" or broken, generate from content
const cleanExcerpt = (excerpt: string | null, content: string): string | null => {
  if (!excerpt || excerpt.trim().match(/^[!\s\-]*$/)) {
    const cleanedContent = content
      .split('\n')
      .filter(line => !line.trim().match(/^!$/))
      .filter(line => !line.match(/^!\[/))
      .filter(line => !line.match(/^-\s*\d{1,2}\s+\w+/))
      .filter(line => !line.match(/^-\s*Comments?\s*(off|on)/i))
      .filter(line => line.trim().length > 0)
      .join(' ')
      .trim();
    
    if (cleanedContent.length === 0) return null;
    const stripped = stripMarkdown(cleanedContent);
    return stripped.slice(0, 150) + (stripped.length > 150 ? '...' : '');
  }
  return stripMarkdown(excerpt);
};

export function LatestNewsSection() {
  const { data: posts, isLoading } = useNewsPosts(3);

  return (
    <section className="py-16 md:py-24">
      <div className="container">
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-center">
          <div>
            <h2 className="font-heading text-3xl font-bold tracking-tight md:text-4xl">
              Latest News
            </h2>
            <p className="mt-2 text-lg text-muted-foreground">
              Updates from our research group.
            </p>
          </div>
          <Button asChild variant="outline">
            <Link to="/news">
              View All News
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>

        <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {isLoading ? (
            <>
              {[1, 2, 3].map((i) => (
                <Card key={i} className="h-full overflow-hidden">
                  <Skeleton className="aspect-video w-full" />
                  <CardHeader>
                    <Skeleton className="h-4 w-32 mb-2" />
                    <Skeleton className="h-6 w-full" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4 mt-2" />
                  </CardContent>
                </Card>
              ))}
            </>
          ) : posts && posts.length > 0 ? (
            posts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link to={`/news/${post.slug}`}>
                  <Card className="group h-full overflow-hidden transition-all hover:shadow-lg hover:-translate-y-1">
                    {post.image_url && (
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={post.image_url}
                          alt={cleanTitle(post.title)}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                          width={433}
                          height={244}
                          loading={index === 0 ? "eager" : "lazy"}
                        />
                      </div>
                    )}
                    <CardHeader>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        {format(parseISO(post.published_at), 'MMMM d, yyyy')}
                      </div>
                      <CardTitle className="font-heading line-clamp-2 group-hover:text-secondary transition-colors pb-0.5">
                        {cleanTitle(post.title)}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="line-clamp-3 text-base">
                        {cleanExcerpt(post.excerpt, post.content)}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))
          ) : (
            <Card className="col-span-full">
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No news posts yet. Check back soon!</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </section>
  );
}